// Typed wrapper around the sfconsole REST API. One place to change if
// the backend JSON shape shifts — every page imports from here so a
// field rename turns into a TypeScript compile error, not a runtime 500.

// ---------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------

export interface Health {
  status: string;
  uptime: string;
  db: boolean;
  version: string;
}

export interface User {
  username: string;
  role: "admin" | "operator" | "readonly";
}

export interface Sensor {
  id: number;
  name: string;
  enabled: boolean;
  protocol: string;
  listen_port: number;
  zone_id: number;
  /** All zones assigned to this sensor (includes primary). Added for multi-zone. */
  zone_ids?: number[];
  listen_ip: string;
  exporter_ip: string;
}

export interface SensorInterface {
  if_index: number;
  name: string;
  direction: string;
  color: string;
}

export interface SensorDetailResp {
  sensor: Sensor;
  interfaces: SensorInterface[];
}

export interface SensorWriteReq {
  name: string;
  enabled: boolean;
  listen_ip: string;
  listen_port: number;
  protocol: "sflow" | "nfv5" | "nfv9" | "ipfix";
  exporter_ip: string;
  zone_id: number;
  /** Additional zones (optional). Backend merges these with zone_id as primary. */
  zone_ids?: number[];
  spool_dir?: string;
  interfaces: Array<{
    if_index: number;
    name: string;
    direction: "auto" | "in" | "out";
    color: string;
  }>;
}

export interface Decoder {
  id: number;
  name: string;
  color: string;
  enabled: boolean;
}

export interface Zone {
  id: number;
  name: string;
  has_ipv6: boolean;
}

export interface ZoneNode {
  id: number;
  parent_id: number;
  prefix: string;
  description: string;
  graphs: boolean;
  accounting: boolean;
}

export interface IPAddrWriteReq {
  prefix: string;
  parent_id?: number;
  description?: string;
  graphs: boolean;
  accounting: boolean;
}

export interface IPRates {
  IPNodeID: number;
  Direction: number;
  BPS: number;
  PPS: number;
  UpdatedAt: string;
}

export interface IPNode {
  id: number;
  prefix: string;
  description: string;
  match: "exact" | "parent" | "child" | "host" | "range" | "zone";
  /** Non-empty when match === "host": the raw IP for per-host series queries. */
  addr?: string;
}

export interface IPSearchResp {
  query: string;
  nodes: IPNode[];
}

export interface IPSeriesResp {
  node_id: number;
  prefix: string;
  description: string;
  points: SeriesPoint[];
}

export interface IPHostSeriesResp {
  addr: string;
  points: SeriesPoint[];
}

export interface IPCIDRSeriesResp {
  prefix: string;
  points: SeriesPoint[];
}

// ── Notification types ────────────────────────────────────────────────────────

export interface NotifChannel {
  id: number;
  name: string;
  type: "telegram" | "webhook";
  config: Record<string, string>;
  enabled: boolean;
  created_at: string;
}

export type AlertMetric = "bps_in" | "bps_out" | "pps_in" | "pps_out";
export type AlertTargetType = "global" | "subnet" | "ip";
export type AlertStatus = "ok" | "pending" | "firing";

export interface AlertRule {
  id: number;
  name: string;
  enabled: boolean;
  target_type: AlertTargetType;
  target_value: string;       // "" for global, "1.2.3.0/24" for subnet
  metric: AlertMetric;
  threshold: number;
  sustained_s: number;
  cooldown_s: number;
  notify_resolve: boolean;
  channel_id: number;
  channel_name: string;       // read-only, from JOIN
  status: AlertStatus;        // read-only, from alert_state
  last_fired: string;         // read-only, ISO or ""
  message_template: string;   // Go text/template; "" = built-in format
  zone_filter_id:   number | null; // null = all zones; only for target_type="ip"
  zone_filter_name: string;        // read-only, resolved zone name
}

export interface NotifHistoryItem {
  id: number;
  rule_id: number;
  rule_name: string;
  channel_id: number;
  channel_name: string;
  event: "firing" | "resolved" | "test";
  value: number;
  message: string;
  sent_at: string;
  ok: boolean;
  error: string;
}

export interface TopTalker {
  rank: number;
  node_id: number;
  prefix: string;
  desc: string;
  bps: number;
  pps: number;
  pct: number;
}

export interface SeriesPoint {
  t: number; // unix seconds
  in: number; // bps
  out: number; // bps
}

// ── Update types ──────────────────────────────────────────────────────────────

export interface UpdateCheck {
  current: string;
  latest: string;
  has_update: boolean;
  changelog: string;
  released_at: string;
}

export interface UpdateStatus {
  state: "idle" | "downloading" | "applying" | "done" | "error";
  message: string;
  version: string;
}

// ── external API types ────────────────────────────────────────────────────────

export interface APIKeyRow {
  id: number;
  name: string;
  key_prefix: string;                    // e.g. "sfk_abc1"
  scopes: Array<"read" | "write" | "admin">;
  created_by: number;
  created_at: number;                    // unix seconds
  expires_at: number | null;             // unix seconds, null = never expires
  last_used_at: number | null;
  last_used_ip?: string;
  enabled: boolean;
}

export interface CreateAPIKeyResp {
  key: APIKeyRow;
  full: string; // the full `sfk_xxxx…` plaintext — shown to the user exactly once
}

export interface APISettings {
  rate_limit_rps: number;
  rate_limit_burst: number;
  audit_retention_days: number;
}

/** Geo feature flags — mirror of settings_geo.go geoSettingsResp */
export interface GeoSettings {
  ixp_enabled: boolean; // collect international (IXP) traffic
  nix_enabled: boolean; // collect domestic-VN (NIX) traffic
}

/** GeoIP database (MaxMind GeoLite2) status + config */
export interface GeoMMDBStatus {
  license_key_set: boolean;   // true = key stored in DB
  auto_update:     boolean;   // weekly background auto-update enabled
  last_updated:    string;    // RFC3339 or ""
  mmdb_bytes:      number;    // Country.mmdb size, 0 if missing
  mmdb_asn_bytes:  number;    // ASN.mmdb size, 0 if missing
  updating:        boolean;   // download in flight
  last_error?:     string;    // last failure message
}

/** One row from /api/v1/live/top/asn */
export interface TopASNRow {
  rank: number;
  asn: number;
  org: string;
  bps: number;
  pps: number;
  pct: number;
}

export interface GeoMMDBPutBody {
  license_key?: string; // omit or "" = keep existing
  auto_update: boolean;
}

export interface APIAccessLogRow {
  id: number;
  key_id: number;
  ts: number;        // unix seconds
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  ip: string;
}

// ── License types ─────────────────────────────────────────────────────────────

export type LicenseMode = "trial" | "licensed" | "grace" | "expired";

export interface LicenseStatus {
  mode: LicenseMode;
  installation_id: string; // send to vendor to get a license key
  customer?: string;
  expires_at?: number;     // unix seconds; undefined in trial mode
  trial_ends_at?: number;  // unix seconds; defined in trial mode
  days_remaining: number;  // positive = days left; negative = overdue
  max_sensors: number;     // 0 = unlimited
  used_sensors: number;
  max_ips: number;         // 0 = unlimited (IP zone entries)
  used_ips: number;
  valid: boolean;
  read_only: boolean;
  message: string;
}

export interface SensorSeries {
  sensor_id: string;
  name: string;
  points: SeriesPoint[];
}

export interface ProtoPoint {
  t: number;
  in:      Record<string, number>;
  out:     Record<string, number>;
  in_pps:  Record<string, number>;
  out_pps: Record<string, number>;
}

export interface IPProtoSeriesResp {
  node_id: number;
  prefix: string;
  description: string;
  decoders: string[];
  points: ProtoPoint[];
}

// ---------------------------------------------------------------------------
// fetch helpers
// ---------------------------------------------------------------------------

// ApiError carries the HTTP status alongside the message so UI code can
// distinguish 401 (unauthenticated) from other failures and redirect to
// the login page.
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!r.ok) {
    let msg = r.statusText;
    try {
      const body = await r.json();
      if (body && typeof body.error === "string") msg = body.error;
    } catch {
      /* not json — fall through */
    }
    throw new ApiError(r.status, msg);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

// ---------------------------------------------------------------------------
// api surface
// ---------------------------------------------------------------------------

export const api = {
  // public
  health: () => request<Health>("/api/v1/health"),
  login: (username: string, password: string) =>
    request<User>("/api/v1/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  // auth-protected
  logout: () => request<{ ok: true }>("/api/v1/logout", { method: "POST" }),
  me: () => request<User>("/api/v1/me"),

  sensors: () => request<Sensor[]>("/api/v1/sensors"),
  sensor: (id: number) => request<SensorDetailResp>(`/api/v1/sensors/${id}`),
  createSensor: (body: SensorWriteReq) =>
    request<{ id: number }>("/api/v1/sensors", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateSensor: (id: number, body: SensorWriteReq) =>
    request<{ id: number }>(`/api/v1/sensors/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteSensor: (id: number) =>
    request<{ ok: true }>(`/api/v1/sensors/${id}`, { method: "DELETE" }),

  decoders: () => request<Decoder[]>("/api/v1/decoders"),

  zones: () => request<Zone[]>("/api/v1/zones"),
  createZone: (name: string, has_ipv6: boolean) =>
    request<{ id: number }>("/api/v1/zones", {
      method: "POST",
      body: JSON.stringify({ name, has_ipv6 }),
    }),
  updateZone: (zoneID: number, name: string, has_ipv6: boolean) =>
    request<{ ok: true }>(`/api/v1/zone/${zoneID}`, {
      method: "PUT",
      body: JSON.stringify({ name, has_ipv6 }),
    }),
  deleteZone: (zoneID: number) =>
    request<{ ok: true }>(`/api/v1/zone/${zoneID}`, { method: "DELETE" }),
  zoneNodes: (zoneID: number) =>
    request<ZoneNode[]>(`/api/v1/zone/${zoneID}/nodes`),
  createZoneNode: (zoneID: number, body: IPAddrWriteReq) =>
    request<{ id: number }>(`/api/v1/zone/${zoneID}/nodes`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateZoneNode: (zoneID: number, nodeID: number, body: IPAddrWriteReq) =>
    request<{ id: number }>(`/api/v1/zone/${zoneID}/nodes/${nodeID}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteZoneNode: (zoneID: number, nodeID: number) =>
    request<{ ok: true }>(`/api/v1/zone/${zoneID}/nodes/${nodeID}`, {
      method: "DELETE",
    }),
  bulkZoneNodes: (zoneID: number, prefixes: string[]) =>
    request<{ inserted: number }>(`/api/v1/zone/${zoneID}/nodes/bulk`, {
      method: "POST",
      body: JSON.stringify({ prefixes }),
    }),

  liveIP: (nodeID: number) => request<IPRates[]>(`/api/v1/live/ip/${nodeID}`),
  ipSearch: (q: string) =>
    request<IPSearchResp>(`/api/v1/ip?q=${encodeURIComponent(q)}`),
  ipNodeSeries: (nodeID: number, seconds = 600) =>
    request<IPSeriesResp>(`/api/v1/ip/${nodeID}/series?seconds=${seconds}`),
  ipHostSeries: (addr: string, seconds = 600) =>
    request<IPHostSeriesResp>(
      `/api/v1/ip/host-series?addr=${encodeURIComponent(addr)}&seconds=${seconds}`
    ),
  ipCIDRSeries: (prefix: string, seconds = 600) =>
    request<IPCIDRSeriesResp>(
      `/api/v1/ip/cidr-series?prefix=${encodeURIComponent(prefix)}&seconds=${seconds}`
    ),
  ipNodeProtoSeries: (nodeID: number, seconds = 600) =>
    request<IPProtoSeriesResp>(`/api/v1/ip/${nodeID}/proto-series?seconds=${seconds}`),
  /** Aggregate per-decoder in/out series across every ipaddr row in a zone. */
  zoneProtoSeries: (zoneID: number, seconds = 600) =>
    request<IPProtoSeriesResp & { zone_id: number; name: string; node_ids: number[] }>(
      `/api/v1/zone/${zoneID}/proto-series?seconds=${seconds}`
    ),
  ipHostProtoSeries: (addr: string, seconds = 600) =>
    request<IPProtoSeriesResp>(`/api/v1/ip/host-proto-series?addr=${encodeURIComponent(addr)}&seconds=${seconds}`),
  ipCIDRProtoSeries: (prefix: string, seconds = 600) =>
    request<IPProtoSeriesResp>(`/api/v1/ip/cidr-proto-series?prefix=${encodeURIComponent(prefix)}&seconds=${seconds}`),
  ipNodeGeoSeries: (nodeID: number, seconds = 600, geoClass: "nix" | "ixp") =>
    request<IPSeriesResp>(`/api/v1/ip/${nodeID}/geo-series?seconds=${seconds}&class=${geoClass}`),
  ipHostGeoSeries: (addr: string, seconds = 600, geoClass: "nix" | "ixp") =>
    request<IPHostSeriesResp>(
      `/api/v1/ip/host-geo-series?addr=${encodeURIComponent(addr)}&seconds=${seconds}&class=${geoClass}`
    ),
  ipCIDRGeoSeries: (prefix: string, seconds = 600, geoClass: "nix" | "ixp") =>
    request<{ prefix: string; points: SeriesPoint[] }>(
      `/api/v1/ip/cidr-geo-series?prefix=${encodeURIComponent(prefix)}&seconds=${seconds}&class=${geoClass}`
    ),
  ipNodeGeoProtoSeries: (nodeID: number, seconds = 600, geoClass: "nix" | "ixp") =>
    request<IPProtoSeriesResp>(`/api/v1/ip/${nodeID}/geo-proto-series?seconds=${seconds}&class=${geoClass}`),
  ipHostGeoProtoSeries: (addr: string, seconds = 600, geoClass: "nix" | "ixp") =>
    request<IPProtoSeriesResp>(
      `/api/v1/ip/host-geo-proto-series?addr=${encodeURIComponent(addr)}&seconds=${seconds}&class=${geoClass}`
    ),
  ipCIDRGeoProtoSeries: (prefix: string, seconds = 600, geoClass: "nix" | "ixp") =>
    request<IPProtoSeriesResp>(
      `/api/v1/ip/cidr-geo-proto-series?prefix=${encodeURIComponent(prefix)}&seconds=${seconds}&class=${geoClass}`
    ),
  liveTop: (metric: "bps" | "pps", dir: "in" | "out", limit: number, geo?: "ixp") =>
    request<TopTalker[]>(
      `/api/v1/live/top?metric=${metric}&dir=${dir}&limit=${limit}${geo ? `&geo=${geo}` : ""}`
    ),

  // Top remote ASNs ranked by bps/pps, scoped to a geo class (ixp default) and direction.
  liveTopASN: (
    geo: "nix" | "ixp",
    dir: "in" | "out",
    metric: "bps" | "pps" = "bps",
    limit = 10
  ) =>
    request<TopASNRow[]>(
      `/api/v1/live/top/asn?geo=${geo}&dir=${dir}&metric=${metric}&limit=${limit}`
    ),

  seriesGlobal: (seconds = 600) =>
    request<SeriesPoint[]>(`/api/v1/series/global?seconds=${seconds}`),

  seriesSensors: (seconds = 600) =>
    request<SensorSeries[]>(`/api/v1/series/sensors?seconds=${seconds}`),

  // Returns per-sensor series for the selected geo class (nix=domestic, ixp=international).
  // Same shape as seriesSensors so LiveChart renders it identically.
  seriesGeo: (seconds = 600, geoClass: "nix" | "ixp") =>
    request<SensorSeries[]>(
      `/api/v1/series/geo?seconds=${seconds}&class=${geoClass}`
    ),

  // ── user management ────────────────────────────────────────────────────────
  listUsers: () =>
    request<{ id: number; username: string; role: string }[]>("/api/v1/users"),

  createUser: (username: string, password: string, role: string) =>
    request<{ id: number; username: string; role: string }>("/api/v1/users", {
      method: "POST",
      body: JSON.stringify({ username, password, role }),
    }),

  updateUserRole: (id: number, role: string) =>
    request<{ ok: boolean }>(`/api/v1/users/${id}`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    }),

  deleteUser: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/users/${id}`, { method: "DELETE" }),

  changePassword: (opts: {
    target_username?: string;
    current_password?: string;
    new_password: string;
  }) =>
    request<{ ok: boolean }>("/api/v1/users/password", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  // ── notifications ──────────────────────────────────────────────────────────
  notifChannels: () =>
    request<NotifChannel[]>("/api/v1/notifications/channels"),
  createNotifChannel: (body: Omit<NotifChannel, "id" | "created_at">) =>
    request<{ id: number }>("/api/v1/notifications/channels", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateNotifChannel: (id: number, body: Omit<NotifChannel, "id" | "created_at">) =>
    request<{ ok: boolean }>(`/api/v1/notifications/channels/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteNotifChannel: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/notifications/channels/${id}`, {
      method: "DELETE",
    }),
  testNotifChannel: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/notifications/channels/${id}/test`, {
      method: "POST",
    }),

  notifRules: () =>
    request<AlertRule[]>("/api/v1/notifications/rules"),
  createNotifRule: (body: Omit<AlertRule, "id" | "channel_name" | "status" | "last_fired" | "zone_filter_name">) =>
    request<{ id: number }>("/api/v1/notifications/rules", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateNotifRule: (id: number, body: Omit<AlertRule, "id" | "channel_name" | "status" | "last_fired" | "zone_filter_name">) =>
    request<{ ok: boolean }>(`/api/v1/notifications/rules/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteNotifRule: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/notifications/rules/${id}`, {
      method: "DELETE",
    }),

  notifHistory: (params?: { limit?: number; rule_id?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.rule_id) qs.set("rule_id", String(params.rule_id));
    const q = qs.toString();
    return request<NotifHistoryItem[]>(`/api/v1/notifications/history${q ? "?" + q : ""}`);
  },

  notifHistoryRetention: () =>
    request<{ days: number }>("/api/v1/notifications/history/retention"),

  setNotifHistoryRetention: (days: number) =>
    request<{ days: number }>("/api/v1/notifications/history/retention", {
      method: "PUT",
      body: JSON.stringify({ days }),
    }),

  flushNotifHistory: () =>
    request<{ ok: boolean; deleted: number }>("/api/v1/notifications/history/flush", {
      method: "POST",
    }),

  // ── admin: data retention & flush ──────────────────────────────────────────
  getRetention: () =>
    request<{ days: number }>("/api/v1/admin/retention"),

  setRetention: (days: number) => {
    const body = new URLSearchParams({ days: String(days) });
    return request<{ ok: boolean; days: number }>("/api/v1/admin/retention", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
  },

  flushData: (olderThanSeconds: number) => {
    const body = new URLSearchParams({ older_than: String(olderThanSeconds) });
    return request<{ ok: boolean; older_than_seconds: number }>("/api/v1/admin/flush", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
  },

  // ── update management ──────────────────────────────────────────────────────
  updateCheck: () =>
    request<UpdateCheck>("/api/v1/update/check"),

  updateApply: (version?: string) =>
    request<{ ok: string; message: string }>("/api/v1/update/apply", {
      method: "POST",
      body: JSON.stringify({ version: version ?? "" }),
    }),

  updateStatus: () =>
    request<UpdateStatus>("/api/v1/update/status"),

  // ── external API: key management & settings ────────────────────────────────
  listAPIKeys: () =>
    request<APIKeyRow[]>("/api/v1/apikeys"),

  createAPIKey: (body: {
    name: string;
    scopes: Array<"read" | "write" | "admin">;
    expires_at?: number | null; // unix seconds
  }) =>
    request<CreateAPIKeyResp>("/api/v1/apikeys", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  revokeAPIKey: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/apikeys/${id}`, { method: "DELETE" }),

  apiSettings: () =>
    request<APISettings>("/api/v1/settings/api"),

  updateAPISettings: (body: APISettings) =>
    request<APISettings>("/api/v1/settings/api", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // ── geo feature toggles ────────────────────────────────────────────────────
  geoSettings: () =>
    request<GeoSettings>("/api/v1/settings/geo"),

  updateGeoSettings: (body: GeoSettings) =>
    request<GeoSettings>("/api/v1/settings/geo", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // ── GeoIP / MaxMind MMDB ──────────────────────────────────────────────────
  geoMMDBStatus: () =>
    request<GeoMMDBStatus>("/api/v1/settings/geo/mmdb"),

  updateGeoMMDB: (body: GeoMMDBPutBody) =>
    request<GeoMMDBStatus>("/api/v1/settings/geo/mmdb", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  triggerGeoMMDBUpdate: () =>
    request<{ status: string }>("/api/v1/settings/geo/mmdb/update", {
      method: "POST",
    }),

  apiKeyUsage: (opts?: { key_id?: number; limit?: number }) => {
    const p = new URLSearchParams();
    if (opts?.key_id != null) p.set("key_id", String(opts.key_id));
    if (opts?.limit != null) p.set("limit", String(opts.limit));
    const qs = p.toString();
    return request<APIAccessLogRow[]>(`/api/v1/apikeys/usage${qs ? "?" + qs : ""}`);
  },

  flushAPIAuditLog: () =>
    request<{ ok: boolean; deleted: number }>("/api/v1/apikeys/usage/flush", {
      method: "POST",
    }),

  // ── license management ─────────────────────────────────────────────────────
  licenseStatus: () =>
    request<LicenseStatus>("/api/v1/license"),

  activateLicense: (key: string) =>
    request<LicenseStatus>("/api/v1/license", {
      method: "POST",
      body: JSON.stringify({ key }),
    }),

  removeLicense: () =>
    request<LicenseStatus>("/api/v1/license", { method: "DELETE" }),
};
